// src/firebase/auth.js
// Servicio de Autenticación de Firebase

import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  updateEmail,
  updatePassword,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification,
  signInWithCustomToken,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from './config';

/**
 * 🔐 SERVICIO DE AUTENTICACIÓN
 * 
 * Funcionalidades:
 * - Login con email/password
 * - Registro de nuevos usuarios
 * - Login con Google
 * - Recuperación de contraseña
 * - Gestión de perfil de usuario
 * - Persistencia de sesión
 */

export const authService = {
  initPersistence() {
    return setPersistence(auth, browserLocalPersistence).catch((error) => {
      console.warn('⚠️ No se pudo fijar la persistencia de auth:', error);
    });
  },
  
  /**
   * 📧 Login con email y contraseña
   */
  async login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('✅ Login exitoso:', userCredential.user.email);
      
      // Actualizar última conexión
      await this.updateLastLogin(userCredential.user.uid);
      
      return userCredential.user;
    } catch (error) {
      console.error('❌ Error en login:', error);
      throw this.handleAuthError(error);
    }
  },

  /**
   * 📝 Registro de nuevo usuario (Profesor)
   */
  async register(userData) {
    const {
      email,
      password,
      nombre,
      apellido,
      asignatura,
      establecimiento,
      accountType = 'profesor'
    } = userData;
    
    try {
      // Crear usuario en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Actualizar perfil con nombre
      await updateProfile(user, {
        displayName: `${nombre} ${apellido}`
      });

      // Enviar email de verificación
      await sendEmailVerification(user);
      
      // Perfil base para control de acceso y rol de experiencia en landing
      await setDoc(doc(db, 'users', user.uid), {
        email: email,
        displayName: `${nombre} ${apellido}`,
        photoURL: user.photoURL || null,
        accessStatus: 'pending',
        role: 'user',
        accountType,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      }, { merge: true });

      // La ficha de profesor solo aplica a cuentas tipo profesor.
      if (accountType === 'profesor') {
        await setDoc(doc(db, 'profesores', user.uid), {
          uid: user.uid,
          email: email,
          nombre: nombre,
          apellido: apellido,
          nombreCompleto: `${nombre} ${apellido}`,
          asignatura: asignatura || 'Música',
          establecimiento: establecimiento || '',
          fotoPerfil: user.photoURL || '',
          rol: 'profesor',
          estado: 'activo',
          emailVerificado: false,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          lastLogin: Timestamp.now(),
          configuracion: {
            notificaciones: true,
            temaOscuro: false,
            idiomaVoces: 'es',
            nivelExperiencia: 'intermedio'
          },
          estadisticas: {
            totalCursos: 0,
            totalEstudiantes: 0,
            totalSesiones: 0,
            totalEvaluaciones: 0
          }
        });
      }

      console.log('✅ Usuario registrado exitosamente:', user.email);
      return user;
      
    } catch (error) {
      console.error('❌ Error en registro:', error);
      throw this.handleAuthError(error);
    }
  },

  /**
   * 🔍 Login con Google
   */
  async loginWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // Verificar si el profesor ya existe en Firestore
      const profesorDoc = await getDoc(doc(db, 'profesores', user.uid));
      
      if (!profesorDoc.exists()) {
        // Crear documento de profesor si es primera vez
        const [nombre, ...apellidoParts] = user.displayName.split(' ');
        const apellido = apellidoParts.join(' ');
        
        await setDoc(doc(db, 'profesores', user.uid), {
          uid: user.uid,
          email: user.email,
          nombre: nombre,
          apellido: apellido,
          nombreCompleto: user.displayName,
          asignatura: 'Música',
          establecimiento: '',
          fotoPerfil: user.photoURL || '',
          rol: 'profesor',
          estado: 'activo',
          emailVerificado: user.emailVerified,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          lastLogin: Timestamp.now(),
          authProvider: 'google',
          
          configuracion: {
            notificaciones: true,
            temaOscuro: false,
            idiomaVoces: 'es',
            nivelExperiencia: 'intermedio'
          },
          
          estadisticas: {
            totalCursos: 0,
            totalEstudiantes: 0,
            totalSesiones: 0,
            totalEvaluaciones: 0
          }
        });

        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          displayName: user.displayName || 'Profesor',
          photoURL: user.photoURL || null,
          accessStatus: 'pending',
          role: 'user',
          accountType: 'profesor',
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        }, { merge: true });
      } else {
        // Actualizar última conexión
        await this.updateLastLogin(user.uid);
      }
      
      console.log('✅ Login con Google exitoso:', user.email);
      return user;
      
    } catch (error) {
      console.error('❌ Error en login con Google:', error);
      throw this.handleAuthError(error);
    }
  },

  /**
   * 🔑 Login con custom token (SSO entre dominios)
   */
  async loginWithCustomToken(token) {
    try {
      if (!token) throw new Error('Token invalido');
      const result = await signInWithCustomToken(auth, token);
      console.log('✅ Login con custom token exitoso:', result.user?.email || result.user?.uid);
      return result.user;
    } catch (error) {
      console.error('❌ Error en login con custom token:', error);
      throw this.handleAuthError(error);
    }
  },

  /**
   * 🚪 Cerrar sesión
   */
  async logout() {
    try {
      await signOut(auth);
      console.log('✅ Sesión cerrada');
    } catch (error) {
      console.error('❌ Error al cerrar sesión:', error);
      throw error;
    }
  },

  /**
   * 🔄 Recuperar contraseña
   */
  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      console.log('✅ Email de recuperación enviado a:', email);
    } catch (error) {
      console.error('❌ Error al enviar email de recuperación:', error);
      throw this.handleAuthError(error);
    }
  },

  /**
   * 📧 Reenviar email de verificación
   */
  async resendVerificationEmail() {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No hay usuario autenticado');
      
      await sendEmailVerification(user);
      console.log('✅ Email de verificación enviado');
    } catch (error) {
      console.error('❌ Error al enviar email de verificación:', error);
      throw error;
    }
  },

  /**
   * 👤 Actualizar perfil de usuario
   */
  async updateUserProfile(profileData) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No hay usuario autenticado');

      // Actualizar en Firebase Auth
      const updates = {};
      if (profileData.displayName) {
        updates.displayName = profileData.displayName;
      }
      if (profileData.photoURL) {
        updates.photoURL = profileData.photoURL;
      }
      
      if (Object.keys(updates).length > 0) {
        await updateProfile(user, updates);
      }

      // Actualizar en Firestore
      const profesorRef = doc(db, 'profesores', user.uid);
      await updateDoc(profesorRef, {
        ...profileData,
        updatedAt: Timestamp.now()
      });

      console.log('✅ Perfil actualizado');
    } catch (error) {
      console.error('❌ Error al actualizar perfil:', error);
      throw error;
    }
  },

  /**
   * 🔑 Cambiar contraseña
   */
  async changePassword(newPassword) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No hay usuario autenticado');

      await updatePassword(user, newPassword);
      console.log('✅ Contraseña actualizada');
    } catch (error) {
      console.error('❌ Error al cambiar contraseña:', error);
      throw this.handleAuthError(error);
    }
  },

  /**
   * 📧 Cambiar email
   */
  async changeEmail(newEmail) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No hay usuario autenticado');

      await updateEmail(user, newEmail);
      
      // Actualizar en Firestore
      const profesorRef = doc(db, 'profesores', user.uid);
      await updateDoc(profesorRef, {
        email: newEmail,
        updatedAt: Timestamp.now()
      });

      console.log('✅ Email actualizado');
    } catch (error) {
      console.error('❌ Error al cambiar email:', error);
      throw this.handleAuthError(error);
    }
  },

  /**
   * 📊 Obtener datos del profesor desde Firestore
   */
  async getProfesorData(uid) {
    try {
      const profesorRef = doc(db, 'profesores', uid);
      const profesorDoc = await getDoc(profesorRef);
      if (profesorDoc.exists()) {
        return {
          id: profesorDoc.id,
          ...profesorDoc.data()
        };
      }

      const authUser = auth.currentUser;
      let userAccountType = null;
      try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          userAccountType = userDoc.data()?.accountType || null;
        }
      } catch {
        // Sin lectura de users: no autocrear ficha de profesor.
      }

      if (userAccountType === 'estudiante') {
        return null;
      }

      try {
        const studentProfileDoc = await getDoc(doc(db, 'bordona_student_profiles', uid));
        if (studentProfileDoc.exists()) {
          return null;
        }
      } catch {
        // Sin lectura de perfil estudiante: no autocrear ficha de profesor.
      }

      const perfilBasico = {
        id: uid,
        email: authUser?.email || '',
        nombre: authUser?.displayName || '',
        asignaturaPrincipal: 'Música',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // Intento de autocreación de perfil para cuentas nuevas.
      try {
        await setDoc(profesorRef, perfilBasico, { merge: true });
      } catch (createError) {
        console.warn('⚠️ No se pudo autocrear perfil del profesor:', createError?.code || createError?.message || createError);
      }

      return perfilBasico;
    } catch (error) {
      const code = String(error?.code || '');
      if (code === 'permission-denied' || code === 'failed-precondition') {
        // Evita que el arranque de sesión falle por reglas/índices temporales.
        // El resto de la app puede continuar con datos mínimos del auth user.
        const authUser = auth.currentUser;
        console.warn('⚠️ Permisos insuficientes para leer perfil profesor; usando perfil básico temporal.');
        return {
          id: uid,
          email: authUser?.email || '',
          nombre: authUser?.displayName || '',
          asignaturaPrincipal: 'Música',
          _perfilTemporal: true
        };
      }

      console.error('❌ Error al obtener datos del profesor:', error);
      throw error;
    }
  },

  /**
   * ⏰ Actualizar última conexión
   */
  async updateLastLogin(uid) {
    try {
      const profesorRef = doc(db, 'profesores', uid);
      const profesorDoc = await getDoc(profesorRef);
      if (!profesorDoc.exists()) return;

      await updateDoc(profesorRef, {
        lastLogin: Timestamp.now()
      });
    } catch (error) {
      // No lanzar error, solo registrar
      console.warn('⚠️ No se pudo actualizar última conexión:', error);
    }
  },

  /**
   * 👂 Observador de estado de autenticación
   */
  onAuthStateChange(callback) {
    return onAuthStateChanged(auth, (user) => {
      // Simplemente pasar el usuario de Firebase Auth
      // El AuthContext se encargará de cargar los datos del profesor
      callback(user);
    });
  },

  /**
   * 🔍 Obtener usuario actual
   */
  getCurrentUser() {
    return auth.currentUser;
  },

  /**
   * ⚠️ Manejo de errores de autenticación
   */
  handleAuthError(error) {
    const errorMessages = {
      'auth/email-already-in-use': 'Este email ya está registrado',
      'auth/invalid-email': 'Email inválido',
      'auth/operation-not-allowed': 'Operación no permitida',
      'auth/weak-password': 'La contraseña debe tener al menos 6 caracteres',
      'auth/user-disabled': 'Esta cuenta ha sido deshabilitada',
      'auth/user-not-found': 'No existe una cuenta con este email',
      'auth/wrong-password': 'Contraseña incorrecta',
      'auth/too-many-requests': 'Demasiados intentos. Intenta más tarde',
      'auth/network-request-failed': 'Error de conexión. Verifica tu internet',
      'auth/requires-recent-login': 'Por seguridad, inicia sesión nuevamente',
      'auth/invalid-credential': 'Credenciales inválidas',
      'auth/popup-closed-by-user': 'Ventana de inicio de sesión cerrada'
    };

    const message = errorMessages[error.code] || error.message;
    return new Error(message);
  }
};

export default authService;
